import React, { FC, useCallback, useEffect } from 'react';
import {
  ITrajectoryDetail,
  TrajectorySectionCode,
  trajectorySectionLabelLookup,
  trajectorySectionorWorksheetTypeCodeLookup,
} from 'src/config/api/models/Trajectory';
import { useParams } from 'react-router';
import { UrlParams } from '../../../types';
import { resetScrollPosition } from 'src/modules/trajectory/helpers';
import TrajectorySectionMenuTab from './TrajetorySectionMenuTab';
import { useUser } from 'src/modules/shared/hooks';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useHistory } from 'react-router';
import { useI18nPath } from 'src/config/clientRoutes/hooks';
import { StyledTabs, StyledTab, StyledFormControl, StyledSelect } from './Atoms';
import { ListItemText, MenuItem, Tooltip } from '@material-ui/core';
import {
  ToolboxPanelModeType,
  setToolboxPanelMode,
  setToolboxPanelOpen,
} from 'src/modules/shared/slices/trajectoryToolbox/trajectoryToolbox';
import { setGeogebraAppName } from 'src/modules/shared/slices/geogebra/entity/geogebraSlice';
import { FontAwesomeSpriteIcon } from '../../FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core';
import {
  getBlockNotes,
  useGetNotesByBookQuery,
  useGetStudentTeacherNotesMutation,
} from 'src/modules/shared/slices/notes/api/notes';
import { setBlockNotes } from 'src/modules/shared/slices/notes/entity/BlockNotesSlice';
import { BlockNote } from 'src/config/api/models/BlockNote';
import { resetBookmark } from 'src/modules/shared/slices/bookmark/entity/bookmarkEntity';
import { resetPresentationMode } from 'src/modules/shared/slices/presentationMode/presentationModeSlice';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';

const buttonTypes: TrajectorySectionCode[] = ['0', '1', '2', '3'];

type TrajectorySectionMenuProps = {
  trajectory: ITrajectoryDetail;
  onSelectedSectionChange: (newSectionCode: TrajectorySectionCode) => void;
  pageId: string | undefined;
  defaultSection?: TrajectorySectionCode;
};

const TrajectorySectionMenu: FC<TrajectorySectionMenuProps> = ({
  trajectory,
  onSelectedSectionChange,
  pageId,
  defaultSection,
}) => {
  // hooks
  const theme = useTheme();
  const { getPath } = useI18nPath();
  const { isAuthor, isTeacher } = useUser();
  const history = useHistory();
  const log = useDataLogging();
  const { bookId, sectionCode, chapterId } = useParams<UrlParams>();

  // redux
  const dispatch = useAppDispatch();
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const isSidePanelOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const studentMode = useAppSelector((state) => state.blockNotes.studentMode);
  const currentStudent = useAppSelector((state) => state.blockNotes.currentStudent);
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );

  // media query
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { refetch: refetchBlockNotes } = getBlockNotes({ pageId }, { skip: isAuthor || !pageId });
  const [getStudentTeacherNotes] = useGetStudentTeacherNotesMutation();
  const { refetch: refetchGroups } = useGetNotesByBookQuery(
    {
      pageId,
      bookId,
    },
    { skip: !isTeacher || !pageId }
  );

  const handleClearPresentationMode = useCallback(() => {
    dispatch(resetPresentationMode());
    const element = document.querySelector('body');
    if (element !== null) {
      if (document.fullscreenElement) {
        log(LogActionTypes.TRAJECTORY_FULLSCREEN_DISABLE);
        document.exitFullscreen().then();
      }
    }
  }, [dispatch, log]);

  const handleDashboardClick = useCallback(() => {
    onSelectedSectionChange('4');
    if (showPresentationMode) {
      handleClearPresentationMode();
    }
    if (bookId && selectedGroup) {
      const scoringPath: string = getPath('studentScoring', {
        bookId: bookId,
        chapterId: chapterId,
        trajectoryId: trajectory.id,
        groupId: selectedGroup,
      });
      history.push(scoringPath);
    }
  }, [
    onSelectedSectionChange,
    showPresentationMode,
    bookId,
    selectedGroup,
    handleClearPresentationMode,
    getPath,
    chapterId,
    trajectory.id,
    history,
  ]);

  useEffect(() => {
    if (isAuthor) return;

    if (studentMode) {
      if (currentStudent && bookId && pageId) {
        getStudentTeacherNotes({ bookId, pageId, studentId: currentStudent.studentId }).then(
          (response) => {
            dispatch(setBlockNotes(response as unknown as BlockNote[]));
          }
        );
      } else {
        refetchGroups();
      }
    } else if (pageId) {
      refetchBlockNotes();
    }
  }, [
    pageId,
    isAuthor,
    studentMode,
    currentStudent,
    bookId,
    getStudentTeacherNotes,
    dispatch,
    refetchGroups,
    refetchBlockNotes,
  ]);

  const handleChangePage = useCallback(
    (newValue: TrajectorySectionCode) => {
      dispatch(resetBookmark());
      dispatch(setGeogebraAppName(''));
      dispatch(setToolboxPanelMode(ToolboxPanelModeType.NONE));
      dispatch(setToolboxPanelOpen(false));
      resetScrollPosition();
      if (showPresentationMode) {
        handleClearPresentationMode();
      }
      onSelectedSectionChange(newValue);
    },
    [dispatch, showPresentationMode, onSelectedSectionChange, handleClearPresentationMode]
  );

  const handleChangePageSelect = useCallback(
    (event: React.ChangeEvent<{ name?: string | undefined; value: TrajectorySectionCode }>) => {
      handleChangePage(event.target.value);
    },
    [handleChangePage]
  );

  const isEntryDisabled = useCallback(
    (type: TrajectorySectionCode) => {
      if (viewMode === 'edit') return false;
      const page = trajectory.pages.find(
        (page) => page.type === trajectorySectionorWorksheetTypeCodeLookup[type]
      );
      if (page === undefined) return true;
      return page.hasContent === false;
    },
    [viewMode, trajectory.pages]
  );

  if (isMobile || (isTablet && activeFilter !== undefined)) {
    return (
      <StyledFormControl>
        <StyledSelect
          id="small-screen-page-select"
          value={defaultSection ? parseInt(defaultSection) : parseInt(sectionCode || '0')}
          onChange={(event) =>
            handleChangePageSelect(
              event as React.ChangeEvent<{
                name?: string | undefined;
                value: TrajectorySectionCode;
              }>
            )
          }
          IconComponent={() => (
            <FontAwesomeSpriteIcon iconSize="md" icon={SpriteIconsLight.CHEVRON_DOWN} />
          )}
        >
          {buttonTypes.map((type: TrajectorySectionCode, index: number) => {
            return (
              <MenuItem
                key={index}
                value={type}
                style={{
                  display: isEntryDisabled(type) || type === sectionCode ? 'none' : 'block',
                }}
              >
                <ListItemText primary={trajectorySectionLabelLookup[type]} />
              </MenuItem>
            );
          })}
        </StyledSelect>
      </StyledFormControl>
    );
  }

  return (
    <StyledTabs
      value={defaultSection ? parseInt(defaultSection) : parseInt(sectionCode || '0')}
      indicatorColor="primary"
      className={isSidePanelOpen ? 'side-panel-open' : ''}
    >
      {buttonTypes.map((type: TrajectorySectionCode, index: number) => {
        return (
          <TrajectorySectionMenuTab
            key={index}
            type={type}
            handleChange={handleChangePage}
            isDisabled={isEntryDisabled(type)}
          />
        );
      })}

      {isTeacher && (viewMode === 'edit' || selectedGroup !== '') && (
        <Tooltip
          title={'Kies eerst een groep'}
          placement="bottom-end"
          disableHoverListener={selectedGroup !== ''}
        >
          <span>
            <StyledTab
              className="attention"
              label="Opvolgen"
              onClick={handleDashboardClick}
              disabled={selectedGroup === ''}
            />
          </span>
        </Tooltip>
      )}
    </StyledTabs>
  );
};

export default TrajectorySectionMenu;
